import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  baseServiceIdOf,
  buildServiceVariant,
  parseServicePriceOptions,
} from '../utils/servicePriceOptions';
import ServiceCatalogPrice from './ServiceCatalogPrice';
import { groupServices as groupCatalogServices } from '../utils/serviceCatalogGroups';
import {
  serviceSessionHeroImage,
  serviceSessionTabThumb,
} from '../../salon/utils/serviceSessionVisuals';
import './RegisterServicePicker.css';

function IconBag() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M6 7h12l-1 12H7L6 7z" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
      <path d="M9 7V5.5A3 3 0 0 1 15 5.5V7" fill="none" stroke="currentColor" strokeWidth="1.5" />
    </svg>
  );
}

function IconHair() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path
        d="M12 3c3.5 1.2 6 4.2 6 8.2 0 2.2-.8 4-2.2 5.4M12 3C8.5 4.2 6 7.2 6 11.2c0 2.2.8 4 2.2 5.4M12 3v14.5M9 20.5c1 .6 2 .9 3 .9s2-.3 3-.9"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
      />
    </svg>
  );
}

function IconSkin() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <circle cx="12" cy="10" r="4.2" fill="none" stroke="currentColor" strokeWidth="1.4" />
      <path
        d="M6.5 19.5c1.6-3 4-4.5 5.5-4.5s3.9 1.5 5.5 4.5"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
      />
    </svg>
  );
}

function IconNail() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path
        d="M8 4.5h3.2c1.2 0 2.2 1 2.2 2.2V14c0 2.3-1.4 4.3-3.5 5.1L8 20V4.5z"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinejoin="round"
      />
      <path d="M8 9.5h5.2" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
    </svg>
  );
}

function iconForService(service) {
  const key = `${service?.id || ''} ${service?.name || ''} ${service?.category || ''}`.toLowerCase();
  if (key.includes('hair') || key.includes('beard') || key.includes('shave')) return <IconHair />;
  if (key.includes('skin') || key.includes('facial')) return <IconSkin />;
  if (key.includes('nail') || key.includes('pedi') || key.includes('mani')) return <IconNail />;
  return <IconBag />;
}

export function groupServices(services, options) {
  return groupCatalogServices(services, options);
}

function selectedForBase(selected, service) {
  const base = String(service?.id || service?.name || '').trim().toLowerCase();
  if (!base) return null;
  return (selected || []).find((item) => baseServiceIdOf(item).toLowerCase() === base) || null;
}

/**
 * Multi-select for register / welcome services (tags or franchise price list).
 * Dual-price services open an option sheet before confirming a variant.
 */
const SERVICE_CARD_PLACEHOLDER = '/no-image.png';

function categoryThumbForGroup(group, visualBundle) {
  if (visualBundle === 'service-session') {
    return serviceSessionTabThumb(group);
  }
  const first = (group?.items || [])[0];
  const fromSvc = String(
    first?.image || first?.image_url || first?.thumbnail || first?.thumb_url || '',
  ).trim();
  return fromSvc || SERVICE_CARD_PLACEHOLDER;
}

function serviceCardImage(service, groupLabel = '', visualBundle, visualGender) {
  if (visualBundle === 'service-session') {
    return serviceSessionHeroImage(visualGender);
  }
  const fromSvc = String(
    service?.image || service?.image_url || service?.thumbnail || service?.thumb_url || '',
  ).trim();
  return fromSvc || SERVICE_CARD_PLACEHOLDER;
}

function ServiceCardStack({
  services: items,
  selected,
  disabled,
  onToggle,
  groupLabel,
  selectedBaseKeys,
  visualBundle,
  visualGender,
  enableQuantity = false,
  onQuantityChange,
  browseOnly = false,
}) {
  return (
    <div className="fi-svc-picker__card-stack" role="group" aria-label={groupLabel}>
      {(items || []).map((service) => {
        const key = String(service.id || service.name);
        const active = selectedBaseKeys.has(
          String(service.id || service.name || '').trim().toLowerCase(),
        );
        const chosen = selectedForBase(selected, service);
        const optionPrefix = chosen?.option_label ? `${chosen.option_label} · ` : '';
        const info = String(service.description || '').trim();
        const qty = Math.max(1, Number(chosen?.quantity) || 1);
        return (
          <div
            key={key}
            className={
              active
                ? 'fi-svc-picker__card-row-wrap is-active'
                : 'fi-svc-picker__card-row-wrap'
            }
          >
            <button
              type="button"
              disabled={disabled}
              className={active ? 'fi-svc-picker__card-row is-active' : 'fi-svc-picker__card-row'}
              onClick={() => {
                if (browseOnly) return;
                onToggle?.(service, chosen);
              }}
              title={info || undefined}
              aria-pressed={browseOnly ? undefined : active}
            >
              <span className="fi-svc-picker__card-row-media">
                <img
                  src={serviceCardImage(service, groupLabel, visualBundle, visualGender)}
                  alt=""
                  className="fi-svc-picker__card-row-img"
                  loading="lazy"
                  decoding="async"
                  onError={(e) => {
                    e.currentTarget.src = SERVICE_CARD_PLACEHOLDER;
                  }}
                />
              </span>
              <span className="fi-svc-picker__card-row-body">
                <span className="fi-svc-picker__card-row-name">{service.name}</span>
                {info ? (
                  <span className="fi-svc-picker__card-row-desc">{info.slice(0, 72)}{info.length > 72 ? '…' : ''}</span>
                ) : null}
              </span>
              <span className="fi-svc-picker__card-row-end">
                <span className="fi-svc-picker__card-row-price">
                  {optionPrefix}
                  <ServiceCatalogPrice service={service} chosen={chosen} />
                </span>
                {browseOnly ? null : (
                  <span
                    className={
                      active
                        ? 'fi-svc-picker__card-row-select is-checked'
                        : 'fi-svc-picker__card-row-select'
                    }
                    aria-hidden="true"
                  />
                )}
              </span>
            </button>
            {enableQuantity && active ? (
              <div
                className="fi-svc-picker__card-row-qty"
                role="group"
                aria-label={`Quantity for ${service.name}`}
              >
                <button
                  type="button"
                  className="fi-svc-picker__qty-btn"
                  disabled={disabled}
                  aria-label="Decrease quantity"
                  onClick={(e) => {
                    e.stopPropagation();
                    onQuantityChange?.(service, -1);
                  }}
                >
                  −
                </button>
                <span className="fi-svc-picker__qty-value" aria-live="polite">{qty}</span>
                <button
                  type="button"
                  className="fi-svc-picker__qty-btn"
                  disabled={disabled}
                  aria-label="Increase quantity"
                  onClick={(e) => {
                    e.stopPropagation();
                    onQuantityChange?.(service, 1);
                  }}
                >
                  +
                </button>
              </div>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}

function ServiceCardGrid({
  services: items,
  selected,
  disabled,
  onToggle,
  groupLabel,
  selectedBaseKeys,
  visualBundle,
  visualGender,
}) {
  return (
    <div className="fi-svc-picker__cards" role="group" aria-label={groupLabel}>
      {(items || []).map((service) => {
        const key = String(service.id || service.name);
        const active = selectedBaseKeys.has(
          String(service.id || service.name || '').trim().toLowerCase(),
        );
        const chosen = selectedForBase(selected, service);
        const optionPrefix = chosen?.option_label ? `${chosen.option_label} · ` : '';
        const info = String(service.description || '').trim();
        return (
          <button
            key={key}
            type="button"
            disabled={disabled}
            className={active ? 'fi-svc-picker__card is-active' : 'fi-svc-picker__card'}
            onClick={() => onToggle?.(service, chosen)}
            title={info || undefined}
            aria-pressed={active}
          >
            <span className="fi-svc-picker__card-media">
              <img
                src={serviceCardImage(service, groupLabel, visualBundle, visualGender)}
                alt=""
                className="fi-svc-picker__card-img"
                loading="lazy"
                decoding="async"
                onError={(e) => {
                  e.currentTarget.src = SERVICE_CARD_PLACEHOLDER;
                }}
              />
              <span
                className={
                  active
                    ? 'fi-svc-picker__card-select is-checked'
                    : 'fi-svc-picker__card-select'
                }
                aria-hidden="true"
              />
            </span>
            <span className="fi-svc-picker__card-body">
              <span className="fi-svc-picker__card-name">{service.name}</span>
              <span className="fi-svc-picker__card-meta">
                <span className="fi-svc-picker__card-price">
                  {optionPrefix}
                  <ServiceCatalogPrice service={service} chosen={chosen} />
                </span>
                {info ? (
                  <span className="fi-svc-picker__card-info" aria-hidden="true">i</span>
                ) : null}
              </span>
            </span>
          </button>
        );
      })}
    </div>
  );
}

function ServiceChipGrid({
  services: items,
  selected,
  disabled,
  onToggle,
  groupLabel,
  selectedBaseKeys,
}) {
  return (
    <div className="fi-svc-picker__chips" role="group" aria-label={groupLabel}>
      {(items || []).map((service) => {
        const key = String(service.id || service.name);
        const active = selectedBaseKeys.has(
          String(service.id || service.name || '').trim().toLowerCase(),
        );
        const chosen = selectedForBase(selected, service);
        const optionPrefix = chosen?.option_label ? `${chosen.option_label} · ` : '';
        return (
          <button
            key={key}
            type="button"
            disabled={disabled}
            className={active ? 'fi-svc-picker__chip is-active' : 'fi-svc-picker__chip'}
            onClick={() => onToggle?.(service, chosen)}
          >
            <span className="fi-svc-picker__chip-icon">{iconForService(service)}</span>
            <span className="fi-svc-picker__chip-text">
              <span className="fi-svc-picker__chip-name">{service.name}</span>
              <span className="fi-svc-picker__chip-price">
                {optionPrefix}
                <ServiceCatalogPrice service={service} chosen={chosen} />
              </span>
            </span>
          </button>
        );
      })}
    </div>
  );
}

function GridForVariant({
  variant,
  visualBundle,
  visualGender,
  cardLayout,
  enableQuantity,
  onQuantityChange,
  ...props
}) {
  if (variant === 'cards') {
    if (cardLayout === 'stack') {
      return (
        <ServiceCardStack
          {...props}
          visualBundle={visualBundle}
          visualGender={visualGender}
          enableQuantity={enableQuantity}
          onQuantityChange={onQuantityChange}
        />
      );
    }
    return <ServiceCardGrid {...props} visualBundle={visualBundle} visualGender={visualGender} />;
  }
  return <ServiceChipGrid {...props} />;
}

function CategoryTabButtons({
  groups,
  activeTab,
  setActiveTab,
  variant,
  visualBundle,
  vertical = false,
}) {
  return groups.map((group, index) => {
    const tabId = `fi-svc-tab-${index}`;
    const isActive = index === activeTab;
    const label = group.category || 'All';
    const count = (group.items || []).length;
    return (
      <button
        key={tabId}
        type="button"
        role="tab"
        id={tabId}
        aria-selected={isActive}
        aria-controls={`${tabId}-panel`}
        tabIndex={isActive ? 0 : -1}
        className={[
          'fi-svc-picker__tab',
          variant === 'cards' ? 'fi-svc-picker__tab--lux' : '',
          vertical ? 'fi-svc-picker__tab--vertical' : '',
          isActive ? 'is-active' : '',
        ]
          .filter(Boolean)
          .join(' ')}
        onClick={() => setActiveTab(index)}
      >
        {variant === 'cards' ? (
          <span className="fi-svc-picker__tab-thumb" aria-hidden="true">
            <img
              src={categoryThumbForGroup(group, visualBundle)}
              alt=""
              loading="lazy"
              decoding="async"
              onError={(e) => {
                e.currentTarget.src = SERVICE_CARD_PLACEHOLDER;
              }}
            />
          </span>
        ) : null}
        <span className="fi-svc-picker__tab-label">{label}</span>
        {count > 0 ? (
          variant === 'cards' ? (
            <span className="fi-svc-picker__tab-count-ring" aria-hidden="true">
              {count}
            </span>
          ) : (
            <span className="fi-svc-picker__tab-count" aria-hidden="true">{count}</span>
          )
        ) : null}
      </button>
    );
  });
}

export default function RegisterServicePicker({
  services = [],
  selected = [],
  onToggle,
  disabled = false,
  title = 'Service list',
  hint = 'Select one or more services (required).',
  previousHistory = [],
  categoryTabs = false,
  categoryTabsLayout = 'horizontal',
  sidebarLeading = null,
  sidebarTrailing = null,
  cardLayout = 'grid',
  variant = 'chips',
  showSearch = false,
  visualBundle = null,
  visualGender = null,
  enableQuantity = false,
  onQuantityChange,
  categoryRailHost = null,
  browseOnly = false,
}) {
  const [sheet, setSheet] = useState(null); // { service, options, selectedKey }
  const [searchQuery, setSearchQuery] = useState('');
  const tabsRef = useRef(null);
  const railScrollRef = useRef(null);
  const filteredServices = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return services;
    return (services || []).filter((item) => {
      const name = String(item?.name || '').toLowerCase();
      const category = String(item?.category || '').toLowerCase();
      return name.includes(q) || category.includes(q);
    });
  }, [services, searchQuery]);
  const groups = groupServices(filteredServices);
  const showGrouped = groups.length > 1 || Boolean(groups[0]?.category);
  const useTabs = categoryTabs && showGrouped && groups.length > 0;
  const [activeTab, setActiveTab] = useState(0);

  useEffect(() => {
    setActiveTab(0);
  }, [services?.length, groups.length]);

  useEffect(() => {
    if (activeTab >= groups.length) {
      setActiveTab(0);
    }
  }, [activeTab, groups.length]);

  const selectedBaseKeys = useMemo(() => {
    const keys = new Set();
    for (const item of selected || []) {
      const base = baseServiceIdOf(item).toLowerCase();
      if (base) keys.add(base);
    }
    return keys;
  }, [selected]);

  const closeSheet = () => setSheet(null);

  const handleChipClick = (service, alreadySelected) => {
    if (disabled || browseOnly) return;
    const already = alreadySelected ?? selectedForBase(selected, service);
    if (already) {
      onToggle?.(already);
      return;
    }
    const options = parseServicePriceOptions(service);
    if (options.length <= 1) {
      const variant =
        options.length === 1 ? buildServiceVariant(service, options[0]) : null;
      onToggle?.(variant || service);
      return;
    }
    setSheet({
      service,
      options,
      selectedKey: options[0]?.key || '',
    });
  };

  const confirmSheet = () => {
    if (!sheet?.service || !sheet.options?.length) {
      closeSheet();
      return;
    }
    const option =
      sheet.options.find((o) => o.key === sheet.selectedKey) || sheet.options[0];
    const variant = buildServiceVariant(sheet.service, option);
    if (variant) onToggle?.(variant);
    closeSheet();
  };

  const scrollTabs = (direction) => {
    const el = tabsRef.current;
    if (!el) return;
    el.scrollBy({ left: direction * 220, behavior: 'smooth' });
  };

  const scrollCategoryRail = (direction) => {
    const el = railScrollRef.current;
    if (!el) return;
    el.scrollBy({ top: direction * 108, behavior: 'smooth' });
  };

  const sidebarCategories = categoryTabsLayout === 'sidebar' && variant === 'cards';
  const detachedCategoryRail = Boolean(sidebarCategories && categoryRailHost);
  const pickerClass = [
    'fi-svc-picker',
    variant === 'cards' ? 'fi-svc-picker--cards' : '',
    sidebarCategories ? 'fi-svc-picker--category-sidebar' : '',
    detachedCategoryRail ? 'fi-svc-picker--category-sidebar-detached' : '',
    cardLayout === 'stack' ? 'fi-svc-picker--card-stack' : '',
    browseOnly ? 'fi-svc-picker--browse' : '',
  ]
    .filter(Boolean)
    .join(' ');

  const categoryRailAside = (
    <aside
      className={[
        'fi-svc-picker__category-rail',
        detachedCategoryRail ? 'fi-svc-picker__category-rail--portaled' : '',
      ]
        .filter(Boolean)
        .join(' ')}
      aria-label="Service categories"
    >
      <div className="fi-svc-picker__category-rail-menu">
        {sidebarLeading ? (
          <div className="fi-svc-picker__category-rail-top">{sidebarLeading}</div>
        ) : null}
        <button
          type="button"
          className="fi-svc-picker__tabs-nav fi-svc-picker__tabs-nav--vertical"
          onClick={() => scrollCategoryRail(-1)}
          aria-label="Scroll categories up"
        >
          ↑
        </button>
        <div className="fi-svc-picker__category-rail-scroll" ref={railScrollRef}>
          <div
            className="fi-svc-picker__tabs fi-svc-picker__tabs--vertical"
            role="tablist"
            aria-label="Service categories"
            ref={tabsRef}
          >
            <CategoryTabButtons
              groups={groups}
              activeTab={activeTab}
              setActiveTab={setActiveTab}
              variant={variant}
              visualBundle={visualBundle}
              vertical
            />
          </div>
        </div>
        <button
          type="button"
          className="fi-svc-picker__tabs-nav fi-svc-picker__tabs-nav--vertical"
          onClick={() => scrollCategoryRail(1)}
          aria-label="Scroll categories down"
        >
          ↓
        </button>
      </div>
      {sidebarTrailing ? (
        <div className="fi-svc-picker__category-rail-footer">{sidebarTrailing}</div>
      ) : null}
    </aside>
  );

  const activeTabPanels = groups.map((group, index) => {
    if (useTabs && index !== activeTab) return null;
    const tabId = `fi-svc-tab-${index}`;
    return (
      <div
        key={group.category || `tab-${index}`}
        id={useTabs ? `${tabId}-panel` : undefined}
        role={useTabs ? 'tabpanel' : undefined}
        aria-labelledby={useTabs ? tabId : undefined}
        className={useTabs ? 'fi-svc-picker__tab-panel' : undefined}
      >
        <GridForVariant
          variant={variant}
          visualBundle={visualBundle}
          visualGender={visualGender}
          enableQuantity={enableQuantity}
          onQuantityChange={onQuantityChange}
          browseOnly={browseOnly}
          cardLayout={cardLayout}
          services={group.items}
          selected={selected}
          disabled={disabled}
          groupLabel={group.category || title}
          selectedBaseKeys={selectedBaseKeys}
          onToggle={handleChipClick}
        />
      </div>
    );
  });

  return (
    <div className={pickerClass}>
      {showSearch ? (
        <div className="fi-svc-picker__toolbar">
          <h3 className="fi-svc-picker__title fi-svc-picker__title--section">{title}</h3>
          <label className="fi-svc-picker__search">
            <span className="fi-svc-picker__search-icon" aria-hidden="true">
              <svg viewBox="0 0 24 24" width="18" height="18">
                <circle cx="11" cy="11" r="6.5" fill="none" stroke="currentColor" strokeWidth="1.6" />
                <path d="M16 16l4.5 4.5" fill="none" stroke="currentColor" strokeWidth="1.6" />
              </svg>
            </span>
            <input
              type="search"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search services…"
              disabled={disabled}
              aria-label="Search services"
            />
          </label>
        </div>
      ) : (
        <div className="fi-svc-picker__head">
          <span className="fi-svc-picker__head-icon" aria-hidden="true">
            <IconBag />
          </span>
          <div>
            <h3 className="fi-svc-picker__title">{title}</h3>
            {hint ? <p className="fi-svc-picker__hint">{hint}</p> : null}
          </div>
        </div>
      )}
      {detachedCategoryRail ? createPortal(categoryRailAside, categoryRailHost) : null}
      {useTabs ? (
        sidebarCategories ? (
          <div
            className={
              detachedCategoryRail
                ? 'fi-svc-picker__category-shell fi-svc-picker__category-shell--detached'
                : 'fi-svc-picker__category-shell'
            }
          >
            {!detachedCategoryRail ? categoryRailAside : null}
            <div className="fi-svc-picker__category-main">{activeTabPanels}</div>
          </div>
        ) : (
          <>
            <div className="fi-svc-picker__tabs-row">
              {variant === 'cards' ? (
                <button
                  type="button"
                  className="fi-svc-picker__tabs-nav"
                  onClick={() => scrollTabs(-1)}
                  aria-label="Scroll categories left"
                >
                  ‹
                </button>
              ) : null}
              <div className="fi-svc-picker__tabs-wrap">
                <div
                  className="fi-svc-picker__tabs"
                  role="tablist"
                  aria-label="Service categories"
                  ref={tabsRef}
                >
                  <CategoryTabButtons
                    groups={groups}
                    activeTab={activeTab}
                    setActiveTab={setActiveTab}
                    variant={variant}
                    visualBundle={visualBundle}
                  />
                </div>
              </div>
              {variant === 'cards' ? (
                <button
                  type="button"
                  className="fi-svc-picker__tabs-nav"
                  onClick={() => scrollTabs(1)}
                  aria-label="Scroll categories right"
                >
                  ›
                </button>
              ) : null}
            </div>
            {activeTabPanels}
          </>
        )
      ) : (
        groups.map((group) => (
          <div
            key={group.category || 'all'}
            className={showGrouped ? 'fi-svc-picker__group' : undefined}
          >
            {showGrouped && group.category ? (
              <p className="fi-svc-picker__group-title">{group.category}</p>
            ) : null}
            <GridForVariant
              variant={variant}
              visualBundle={visualBundle}
              visualGender={visualGender}
              enableQuantity={enableQuantity}
              onQuantityChange={onQuantityChange}
              browseOnly={browseOnly}
              cardLayout={cardLayout}
              services={group.items}
              selected={selected}
              disabled={disabled}
              groupLabel={group.category || title}
              selectedBaseKeys={selectedBaseKeys}
              onToggle={handleChipClick}
            />
          </div>
        ))
      )}
      {!services?.length ? (
        <p className="fi-svc-picker__hint">No services configured yet.</p>
      ) : null}
      {previousHistory?.length ? (
        <div className="fi-svc-picker__history">
          <p className="fi-svc-picker__history-title">Previous services</p>
          <ul>
            {previousHistory.slice(0, 8).map((item) => (
              <li key={item.id || `${item.service_name}-${item.selected_at}`}>
                <strong>{item.service_name || item.name}</strong>
                {item.selected_at ? (
                  <span>
                    {typeof item.selected_at === 'string'
                      ? item.selected_at.slice(0, 10)
                      : ''}
                  </span>
                ) : null}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {sheet ? (
        <div className="fi-svc-picker__sheet-backdrop" role="presentation" onClick={closeSheet}>
          <div
            className="fi-svc-picker__sheet"
            role="dialog"
            aria-modal="true"
            aria-label="Choose price option"
            onClick={(e) => e.stopPropagation()}
          >
            <p className="fi-svc-picker__sheet-title">{sheet.service?.name || 'Choose option'}</p>
            <p className="fi-svc-picker__sheet-hint">Select one price, then confirm.</p>
            <div className="fi-svc-picker__sheet-options" role="radiogroup">
              {sheet.options.map((opt) => {
                const active = sheet.selectedKey === opt.key;
                return (
                  <button
                    key={opt.key}
                    type="button"
                    role="radio"
                    aria-checked={active}
                    className={
                      active
                        ? 'fi-svc-picker__sheet-option is-active'
                        : 'fi-svc-picker__sheet-option'
                    }
                    onClick={() => setSheet((prev) => ({ ...prev, selectedKey: opt.key }))}
                  >
                    <span>{opt.label || 'Price'}</span>
                    <strong>{opt.displayPrice}</strong>
                  </button>
                );
              })}
            </div>
            <div className="fi-svc-picker__sheet-actions">
              <button type="button" className="fi-svc-picker__sheet-cancel" onClick={closeSheet}>
                Cancel
              </button>
              <button type="button" className="fi-svc-picker__sheet-confirm" onClick={confirmSheet}>
                Confirm
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
